// The glossary's markdown bodies, keyed by path ('./glossary/sharpe.md'). One file per term so the copy can be
// edited without touching code; the drawer renders the name, so the files carry no title.
const MARKDOWN = import.meta.glob<string>('./glossary/*.md', { query: '?raw', import: 'default', eager: true });

// The Portfolio Reporting redesign's order: the summary KPIs first, then the risk and relative statistics.
const TERMS = [
    ['mv', 'Market Value'],
    ['nic', 'Net Invested Capital'],
    ['ne', 'Net Earnings'],
    ['twr', 'Net Return (TWR)'],
    ['ann', 'Annualized Return'],
    ['ret', 'Return'],
    ['sd', 'Standard Deviation'],
    ['sharpe', 'Sharpe Ratio'],
    ['beta', 'Beta'],
    ['alpha', 'Alpha (Jensen’s)'],
    ['sortino', 'Sortino Ratio'],
    ['dd', 'Downside Deviation'],
    ['te', 'Tracking Error'],
    ['ir', 'Information Ratio'],
    ['treynor', 'Treynor Ratio (%)'],
    ['up', 'Up Capture (%)'],
    ['down', 'Down Capture (%)'],
    ['r2', 'R² (%)'],
] as const;

export type GlossaryKey = (typeof TERMS)[number][0];

// Every term needs its file, so a missing or empty one fails at load instead of rendering a blank entry.
export const GLOSSARY_TERMS: ReadonlyArray<{ key: GlossaryKey; name: string; markdown: string }> = TERMS.map(
    ([key, name]) => {
        const markdown = MARKDOWN[`./glossary/${key}.md`];
        if (!markdown?.trim()) {
            throw new Error(`Missing glossary/${key}.md`);
        }
        return { key, name, markdown };
    },
);

// Takes any string: the key comes from the URL, so it may not be a GlossaryKey.
export const findGlossaryTerm = (key: string | null) => GLOSSARY_TERMS.find((term) => term.key === key);

// The term before or after `key`, wrapping past either end so prev/next never dead-ends. Adding the length
// before the modulo keeps a −1 step from the first term at the last index instead of a negative one.
export const adjacentGlossaryKey = (key: GlossaryKey, step: 1 | -1): GlossaryKey => {
    const index = GLOSSARY_TERMS.findIndex((term) => term.key === key);
    return GLOSSARY_TERMS[(index + step + GLOSSARY_TERMS.length) % GLOSSARY_TERMS.length].key;
};
